import {
  PROD_HOMEPAGE_HOSTNAME,
  STAGING_HOMEPAGE_HOSTNAME,
} from '@diamond/shared/environments';
import { RecipeDetailResponse } from '@diamond/shared/types';
import { GetServerSideProps } from 'next';

import { getAuthStateFromServer } from '../auth/auth';

export type RecommendedProduct =
  RecipeDetailResponse['ingredient_groups'][number]['ingredients'][number]['recommended_products'];
export type AlternatifProduct =
  RecipeDetailResponse['ingredient_groups'][number]['ingredients'][number]['alternative_products'];

export function handleCogSummary(
  data: RecommendedProduct | AlternatifProduct,
  isSum: boolean
) {
  if (isSum) {
    const cogSummary = data
      .map((reko) => reko.cogs_per_portion)
      .reduce((acc, cur) => acc + cur, 0);

    return cogSummary;
  }

  const cogSummary = data.map((reko) => reko.cogs_per_portion).length;

  return cogSummary;
}

export function getPublicHomepageRecipesOrigin(): string {
  const fromEnv = process.env['NX_HOMEPAGE_BASE_URL'] || process.env['SOL_HOMEPAGE_BASE_URL'];
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }
  if ((process.env['NX_API_URL'] ?? '').includes('staging')) {
    return `https://${STAGING_HOMEPAGE_HOSTNAME}`;
  }
  return `https://${PROD_HOMEPAGE_HOSTNAME}`;
}

export function shouldRedirectPublicRecipesToHomepage(): boolean {
  return process.env['NEXT_PUBLIC_REDIRECT_PUBLIC_RECIPES_TO_HOMEPAGE'] === 'true';
}

export const getRecipeListServerProps: GetServerSideProps = async (context) => {
  const auth = await getAuthStateFromServer(context);
  return maybeRedirectGuestRecipes(auth, '/recipes');
};

export const getRecipeDetailServerProps: GetServerSideProps = async (context) => {
  const auth = await getAuthStateFromServer(context);
  const slug = context.params?.['recipesId'];
  const path =
    typeof slug === 'string' && slug.length > 0 ? `/recipes/${encodeURIComponent(slug)}` : '/recipes';
  return maybeRedirectGuestRecipes(auth, path);
};

function maybeRedirectGuestRecipes(
  auth: Awaited<ReturnType<typeof getAuthStateFromServer>>,
  homepagePath: string
) {
  if ('redirect' in auth || 'notFound' in auth) {
    return auth;
  }

  if (
    shouldRedirectPublicRecipesToHomepage() &&
    auth.props &&
    'isAuthenticated' in auth.props &&
    !auth.props.isAuthenticated
  ) {
    return {
      redirect: {
        destination: `${getPublicHomepageRecipesOrigin()}${homepagePath}`,
        permanent: false,
      },
    };
  }

  return auth;
}
